import {NullString, UUID} from "../../interfaces/general";
import {useEffect, useState} from "react";
import {ProcessingRunData, TicketLogEntry, TicketProcessingEntry} from "../../interfaces/processing";
import {
  getLogById,
  getProcessingEntry,
  getProcessingRuns, postRerunTicketLog, setLogBugSeverity, setLogReadyForTesting,
  setTicketAsBug,
  verifyReplaceImages
} from "../../services/processingLogsService";
import {Comment} from "../../interfaces/ticket";
import {runInitialTicketTest} from "../../services/testService";
import { ShopifyOrder, ShopifyProduct } from "../../interfaces/shopify";
import { getProductsByIds } from "../../services/shopifyService";

export interface UseProcessingEntry {
  (
    logId: UUID | null | undefined,
    userId: UUID | null | undefined
  ): {
    processingEntry: TicketProcessingEntry | null,
    logEntry: TicketLogEntry | null,
    loadError: string | null,
    changeSelectedMessages: ChangeSelectedMessages,
    allComments: Comment[],
    selectedCommentIds: number[],
    selectedTestMessages: Comment[],
    runTicketTest: () => void,
    isTestRunning: boolean,
    testError: NullString,
    processingRuns: ProcessingRunData[],
    reloadProcessingEntry: () => void,
    saveTicketAsBug: () => void,
    confirmVerifyImages: ConfirmVerifyImages,
    setBugSeverity: SetBugSeverity,
    setReadyForTest: SetReadyForTest,
    showOrderModal: ShowOrderModal,
    orderModalData: OrderModalData | null,
    setOrderModalData: (data: OrderModalData | null) => void,
    rerunTicketLog: RerunTicketLog
  }
}

export interface ChangeSelectedMessages {
  (messageId: number): void
}

export interface ConfirmVerifyImages {
  (
    processingEntryId: UUID,
    isVerified: boolean,
    isTest: boolean,
  ): Promise<{
    success: boolean,
    error?: NullString
  }>
}

export interface SetBugSeverity {
  (severity: number): Promise<{
    success: boolean,
    error?: string
  }>
}

export interface SetReadyForTest {
  (isReady: boolean): Promise<{
    success: boolean,
    error?: string
  }>
}

export interface OrderModalData {
  orderData: ShopifyOrder,
  productsData: ShopifyProduct[]
}

export interface ShowOrderModal {
  (orderData: ShopifyOrder): void
}

export interface RerunTicketLog {
  (isTest: boolean): void
}

const useProcessingEntry: UseProcessingEntry = (logId, userId) => {
  const [processingEntry, setProcessingEntry] = useState<TicketProcessingEntry | null>(null);
  const [logEntry, setLogEntry] = useState<TicketLogEntry | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [selectedCommentIds, setSelectedCommentIds] = useState<number[]>([]);
  const [selectedTestMessages, setSelectedTestMessages] = useState<Comment[]>([]);
  const [isTestRunning, setIsTestRunning] = useState<boolean>(false);
  const [testError, setTestError] = useState<NullString>(null);
  const [processingRuns, setProcessingRuns] = useState<ProcessingRunData[]>([]);
  const [orderModalData, setOrderModalData] = useState<OrderModalData | null>(null);

  useEffect(() => {
    if (logId) {
      loadProcessingEntry(logId, userId);
      loadProcessingRuns(logId, userId);
    }
  }, [logId]);

  async function loadProcessingEntry(id: UUID, userId: UUID | null | undefined) {
    try {
      const entryResponse = await getProcessingEntry(id, userId);
      const ticketLogRes = await getLogById(id);

      if (ticketLogRes.status === 200) {
        setLogEntry(ticketLogRes.data?.log_entry || null);
      }

      if (entryResponse.status === 200) {
        setProcessingEntry(entryResponse.data);

        const commentsList: Array<Comment> = entryResponse.data?.ticket_data?.comments_data?.comments;
        if (Array.isArray(commentsList)) {
          const publicComments = commentsList.filter((comment) => {
            return comment.public
          });
          setAllComments(publicComments);
        }
      } else {
        console.log('entryResponse.data?.detail')
        console.log(entryResponse.data?.detail)
        setLoadError(entryResponse.data?.detail || 'Failed to load processing entry');
      }
    } catch (error: { detail?: string } | any) {
      console.error(error);
      const errorMsg = error?.detail || 'Failed to load processing entry';
      setLoadError(`${errorMsg}`);
    }
  }

  const reloadProcessingEntry = async () => {
    if (logId && userId) {
      await loadProcessingEntry(logId, userId);
    }
  };

  async function loadProcessingRuns(id: UUID, userId: UUID | null | undefined) {
    try {
      const loadRes = await getProcessingRuns(id, userId);

      if (loadRes.status === 200) {
        setProcessingRuns(loadRes.data.processing_runs);
      } else {
        setLoadError(loadRes.data?.detail || 'Failed to load processing runs');
      }
    } catch (error: { detail?: string } | any) {
      console.error(error);
      const errorMsg = error?.detail || 'Failed to load processing entry';
      setLoadError(`${errorMsg}`);
    }
  }

  const changeSelectedMessages: ChangeSelectedMessages = (messageId) => {
    if (selectedCommentIds.includes(messageId)) {
      const updatedIdsList = selectedCommentIds.filter(id => id !== messageId);
      const updatedMessagesList = selectedTestMessages.filter(msgDict => {
        return msgDict.id !== messageId;
      });
      setSelectedCommentIds(updatedIdsList);
      setSelectedTestMessages(updatedMessagesList);
    } else {
      const commentDict = allComments.find(comment => comment.id === messageId);
      if (!commentDict) {
        return;
      }
      setSelectedCommentIds([...selectedCommentIds, messageId]);
      setSelectedTestMessages([...selectedTestMessages, commentDict]);
    }
  };

  async function runTicketTest() {
    try {
      setIsTestRunning(true);
      let testData: {
        ticket_data: TicketProcessingEntry | null,
        selected_messages: Comment[],
        user_id?: UUID
      } = {
        ticket_data: processingEntry,
        selected_messages: selectedTestMessages,
      };
      if (userId) {
        testData.user_id = userId;
      }
      const result = await runInitialTicketTest(testData);

    } catch (err) {
      setIsTestRunning(false);
      console.log(err);
      setTestError(`${err}`);
    }
  }

  const saveTicketAsBug = async () => {
    if (!processingEntry) {
      return;
    }
    const newIsBug = !processingEntry?.is_bug;
    const updateRes = await setTicketAsBug(processingEntry?.id, newIsBug);

    if (updateRes.status === 200) {
      reloadProcessingEntry();
    } else {
      console.log('Error saving ticket as bug');
    }
  };

  const confirmVerifyImages: ConfirmVerifyImages = async (
    processingEntryId,
    isVerified,
    isTest
  ) => {
    try {
      const externalId = processingEntry?.external_id;
      if (!externalId) {
        return {
          success: false,
          error: 'No external ID found'
        }
      }
      const response = await verifyReplaceImages(
        processingEntryId,
        externalId,
        isVerified,
        isTest,
      );

      if (response.status === 200) {
        return {
          success: true
        }
      } else {
        return {
          success: false,
          error: response.data?.detail || 'Failed to confirm images'
        }
      }
    } catch (error) {
      console.error(error);
      return {
        success: false,
        error: `${error}`
      }
    }
  }

  const setBugSeverity: SetBugSeverity = async (severity) => {
    try {
      if (!logEntry) {
        return {
          success: false,
          error: 'No log entry found'
        }
      }
      const response = await setLogBugSeverity(logEntry.id, severity);

      if (response.status === 200) {
        reloadProcessingEntry();
        return {
          success: true
        }
      } else {
        return {
          success: false,
          error: response.data?.detail || 'Failed to set bug severity'
        }
      }
    } catch (error) {
      console.error(error);
      return {
        success: false,
        error: `${error}`
      }
    }
  };

  const setReadyForTest: SetReadyForTest = async (isReady) => {
    try {
      if (!logEntry) {
        return {
          success: false,
          error: 'No log entry found'
        }
      }
      const response = await setLogReadyForTesting(logEntry.id, isReady);

      if (response.status === 200) {
        reloadProcessingEntry();
        return {
          success: true
        }
      } else {
        return {
          success: false,
          error: response.data?.detail || 'Failed to set ready for test'
        }
      }
    } catch (error) {
      console.error(error);
      return {
        success: false,
        error: `${error}`
      }
    }
  };

  const showOrderModal: ShowOrderModal = async (orderData) => {
    const productsIds = Array.isArray(orderData.line_items) ? orderData.line_items.map((item) => {
      return item.product_id;
    }) : [];
    const productsRes = await getProductsByIds(productsIds);
    if (productsRes.status === 200) {
      setOrderModalData({
        orderData,
        productsData: productsRes.data?.products || []
      });
    }
  };

  const rerunTicketLog: RerunTicketLog = async (isTest: boolean) => {
    if (!logId) {
      console.log('No logId or userId');
      return;
    }
    const response = await postRerunTicketLog(logId, isTest);

    if (response.status === 200) {
      loadProcessingEntry(logId, userId);
      loadProcessingRuns(logId, userId);
    }
  };

  return {
    processingEntry,
    logEntry,
    loadError,
    changeSelectedMessages,
    allComments,
    selectedCommentIds,
    selectedTestMessages,
    runTicketTest,
    isTestRunning,
    testError,
    processingRuns,
    reloadProcessingEntry,
    saveTicketAsBug,
    confirmVerifyImages,
    setBugSeverity,
    setReadyForTest,
    showOrderModal,
    orderModalData,
    setOrderModalData,
    rerunTicketLog
  }
};

export default useProcessingEntry;
